/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useMemo } from 'react';
import ReactSelect from 'react-select';

import { AnyObject } from 'types';
import { getFirst } from 'utils/array';

import { useIsBreakpoint, useToggleV2 } from 'hooks';

import { customComponents } from './Components';
import { MobileSelectWrap } from './Responsive';
import { getClassNames, styles } from './styles';

import { SelectOption, SelectProps } from './types';
import { buildChangeEventLike } from '../types';
import { useLang } from 'locale';

function determineValueKey(entity: AnyObject, key?: string) {
  if (key) return key;

  if ('id' in entity) return 'id';

  return 'value';
}

export function Select<Entity extends AnyObject>({
  id,
  name,
  disabled,
  placeholder,
  options,
  groupedOptions,
  isSearchable = true,
  isMulti,
  labelKey = 'label',
  valueKey,
  disabledKey,
  noOptionMessage,
  hideSelectedOptions = false,
  formatLabel,
  onChange,
  onValueChange,
  value,
  closeOnSelect,
  mobileTitle,
  anchorTo,
  ...variantProps
}: SelectProps<Entity>) {
  const { t } = useLang();

  const isGrouped = useMemo(() => {
    return !!groupedOptions;
  }, [groupedOptions]);

  const reactSelectOptions = useMemo(() => {
    const fixOptions = (entities: Entity[]): SelectOption[] => {
      if (!entities.length) return [];

      const actualValueKey = determineValueKey(getFirst(entities), valueKey as string);

      return entities.map((entity) => ({
        value: entity[actualValueKey],
        label: entity[labelKey],
        isDisabled: disabledKey ? !!entity[disabledKey] : undefined,
        entity,
      }));
    };

    return isGrouped
      ? groupedOptions!.map((group) => ({
          ...group,
          options: fixOptions(group.options),
        }))
      : fixOptions(options as Entity[]);
  }, [options, labelKey, valueKey, disabledKey, isGrouped, groupedOptions]);

  const actualValue = useMemo(() => {
    if (!value) return;

    const allOptions = reactSelectOptions
      .map((option) => (isGrouped ? (option as { options: SelectOption[] }).options : option))
      .flat() as SelectOption[];

    const reactSelectValue = isMulti
      ? allOptions.filter((option) => value.includes(option.value as string))
      : allOptions.find((option) => option.value === value);

    return reactSelectValue;
  }, [isGrouped, reactSelectOptions, isMulti, value]);

  const handleChange = useCallback(
    (value: SelectOption | SelectOption[]) => {
      const fireChange = (upValue: string | string[]): any => {
        // We simplify adding as any due to the isMulti prop
        onValueChange?.(upValue as any);

        onChange?.(
          buildChangeEventLike<any>({
            name,
            id,
            value: upValue,
          }),
        );
      };

      if (!value) return fireChange(isMulti ? [] : '');

      const updatedValue = Array.isArray(value)
        ? value.map((option) => option.value)
        : (value as SelectOption).value;

      fireChange(updatedValue);
    },
    [onChange, isMulti, onValueChange, id, name],
  );

  const isSmallTablet = useIsBreakpoint('SMALL_TABLET');
  const [drawerOpen, { turnOff, turnOn }] = useToggleV2();

  return isSmallTablet ? (
    <MobileSelectWrap
      close={turnOff}
      formatLabel={formatLabel}
      isOpen={drawerOpen}
      isSearchable={isSearchable}
      isGrouped={isGrouped}
      isMulti={isMulti}
      options={reactSelectOptions}
      value={actualValue}
      onChange={handleChange}
      title={mobileTitle}
    >
      <ReactSelect
        // -- MOBILE DIFFERENCES
        onMenuOpen={turnOn}
        menuIsOpen={false}
        isSearchable={false}
        // -- MOBILE DIFFERENCES
        unstyled
        menuPlacement="auto"
        menuPosition="fixed"
        minMenuHeight={200}
        name={name}
        hideSelectedOptions={hideSelectedOptions}
        id={id}
        isDisabled={disabled}
        inputId={id}
        noOptionsMessage={() => noOptionMessage}
        options={reactSelectOptions}
        components={customComponents}
        styles={styles}
        formatOptionLabel={
          formatLabel ? (option) => formatLabel?.((option as SelectOption).entity) : undefined
        }
        isMulti={isMulti}
        placeholder={placeholder || t('select')}
        closeMenuOnSelect={closeOnSelect}
        classNames={getClassNames(variantProps)}
        {...(value !== undefined || onChange || onValueChange
          ? {
              value: actualValue ?? null,
              onChange: handleChange as any,
            }
          : {})}
      />
    </MobileSelectWrap>
  ) : (
    <ReactSelect
      unstyled
      menuPlacement="auto"
      menuPosition="fixed"
      minMenuHeight={200}
      name={name}
      hideSelectedOptions={hideSelectedOptions}
      id={id}
      isDisabled={disabled}
      inputId={id}
      noOptionsMessage={() => noOptionMessage}
      options={reactSelectOptions}
      components={customComponents}
      styles={styles}
      formatOptionLabel={
        formatLabel ? (option) => formatLabel?.((option as SelectOption).entity) : undefined
      }
      isSearchable={isSearchable}
      isMulti={isMulti}
      placeholder={placeholder || t('select')}
      closeMenuOnSelect={closeOnSelect}
      classNames={getClassNames({ ...variantProps, anchoredOutside: !!anchorTo })}
      menuPortalTarget={anchorTo === 'body' ? document.body : anchorTo}
      {...(value !== undefined || onChange || onValueChange
        ? {
            value: actualValue ?? null,
            onChange: handleChange as any,
          }
        : {})}
    />
  );
}
